import {
	type PageTypes,
	useSetPageShowTopBarRPBalance,
	useSetPageTitle,
	useSetPageTopBarHeaderLeft,
	useSetPageTopBarHeaderRight,
	useSetPageType
} from '@capsicum/common';
import { type ReactNode } from 'react';
import styled from 'styled-components';
import { match } from 'ts-pattern';

import { ResponsiveContainer, TOPBAR_HEIGHT } from '../../atoms';

import { PageSidePanel } from './PageSidePanel';

type BasePageContainerProps = {
	body: JSX.Element;
	fullWidth?: boolean;
	showTopBarRPBalance?: boolean;
	sidePanel?: JSX.Element;
	title?: string;
	topBarHeaderLeft?: Array<ReactNode> | ReactNode;
	topBarHeaderRight?: Array<ReactNode> | ReactNode;
};

export type PageContainerProps = BasePageContainerProps & {
	variant?: PageTypes;
};

/**
 * PageContainer generic component
 * @param {PageContainerProps} props - Component's props
 * @param {JSX.Element} props.body body main content
 * @param {JSX.Element} props.sidePanel sidePanel right side panel
 * @param {string} [props.title] title of page to show
 * @param {ReactNode[]} [props.topBarHeaderRight] actions to show on the page's right top bar
 * @param {ReactNode[]} [props.topBarHeaderLeft] actions to show on the page's left top bar
 * @param {('page'|'subpage'|'background-view')} [props.variant = page] type of page to show
 * @param {BackgroundViewProps} [props.backgroundViewProps] props to pass to BackgroundView component
 */
export function PageContainer({
	body,
	fullWidth = false,
	showTopBarRPBalance = true,
	sidePanel,
	title,
	topBarHeaderLeft,
	topBarHeaderRight,
	...props
}: PageContainerProps) {
	const { variant = 'page' } = props;

	useSetPageType(sidePanel ? 'page-with-sidePanel' : variant);
	useSetPageTitle(title);
	useSetPageTopBarHeaderRight(topBarHeaderRight);
	useSetPageTopBarHeaderLeft(topBarHeaderLeft);
	useSetPageShowTopBarRPBalance(showTopBarRPBalance);

	if (sidePanel) {
		return (
			<>
				<S.PageResponsiveContainer $fullWidth={fullWidth} $variant={variant}>
					{body}
				</S.PageResponsiveContainer>
				{sidePanel}
			</>
		);
	}

	return match(props)
		.with({ variant: 'subpage' }, () => (
			<S.PageResponsiveContainer $fullWidth={fullWidth} $variant={variant}>
				{body}
			</S.PageResponsiveContainer>
		))
		.with({ variant: 'subpage-with-background' }, () => body)
		.with({ variant: 'background-view' }, () => body)
		.otherwise(() => (
			<>
				<S.PageResponsiveContainer $fullWidth={fullWidth} $variant={variant}>
					{body}
				</S.PageResponsiveContainer>
				<PageSidePanel forceRender />
			</>
		));
}

namespace S {
	export const PageResponsiveContainer = styled(ResponsiveContainer)<{ $fullWidth: boolean }>`
		min-height: calc(100dvh - ${TOPBAR_HEIGHT}px);

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			border-right: ${({ $variant, theme }) => ($variant === 'subpage' ? '0' : theme.border.width.small)} solid;
			border-left: ${({ $variant, theme }) => ($variant === 'subpage' ? '0' : theme.border.width.small)} solid;
			border-color: ${({ theme }) => theme.colors.borders.main.divider};
		}

		${({ $fullWidth }) => ($fullWidth ? 'width: 100%; max-width: none' : '')};
	`;
}
